'use client';

import { useEffect, useId, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Checkbox } from '@/components/ui/checkbox/checkbox.component';
import { DatePicker } from '@/components/ui/calendar/date-picker.component';
import { useDateRangeFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-date-range-filter.hook';
import { SearchFilterDateValues } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-date.type';

const DATE_PLACEHOLDER = 'DD/MM/AAAA';

const MISSING_END_DATE_MESSAGE = 'Informe a data final do período';

interface DateFilterSectionProps {
  values: Readonly<SearchFilterDateValues>;
  onChange: (values: Partial<SearchFilterDateValues>) => void;
  onValidityChange?: (isValid: boolean) => void;
}

export function DateFilterSection({ values, onChange, onValidityChange }: DateFilterSectionProps) {
  const labelId = useId();
  const {
    startDate,
    endDate,
    isRangeEnabled,
    isRangeToggleDisabled,
    isEndDateMissing,
    shouldShowEndDateError,
    minStartDate,
    minEndDate,
    setStartDate,
    setEndDate,
    toggleRange,
    visitEndDate,
  } = useDateRangeFilter({ values, onChange });

  const [isStartDateValid, setIsStartDateValid] = useState(true);
  const [isEndDateValid, setIsEndDateValid] = useState(true);

  const isValid = isStartDateValid && !isEndDateMissing && (!isRangeEnabled || isEndDateValid);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  return (
    <section
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-4"
      data-testid={DataTestIdEnum.SEARCH_FILTERS_DATE}
    >
      <DatePicker
        label="Quando você quer agendar?"
        labelId={labelId}
        placeholder={DATE_PLACEHOLDER}
        value={startDate}
        onChange={setStartDate}
        onValidityChange={setIsStartDateValid}
        minDate={minStartDate}
        calendarProps={{ startMonth: minStartDate }}
        dataTestId={DataTestIdEnum.SEARCH_FILTERS_DATE_START}
      />

      <Checkbox
        container="box"
        checked={isRangeEnabled}
        disabled={isRangeToggleDisabled}
        onCheckedChange={(checked) => toggleRange(checked === true)}
        data-testid={DataTestIdEnum.SEARCH_FILTERS_DATE_RANGE_TOGGLE}
      >
        Selecionar período
      </Checkbox>

      {isRangeEnabled && (
        <DatePicker
          required
          label="Até quando?"
          placeholder={DATE_PLACEHOLDER}
          value={endDate}
          onChange={setEndDate}
          onValidityChange={setIsEndDateValid}
          onBlur={visitEndDate}
          minDate={minEndDate}
          calendarProps={{ startMonth: minEndDate }}
          error={shouldShowEndDateError ? MISSING_END_DATE_MESSAGE : undefined}
          dataTestId={DataTestIdEnum.SEARCH_FILTERS_DATE_END}
        />
      )}
    </section>
  );
}
